import * as React from 'react';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import {type VariantProps} from 'class-variance-authority';
import {cn} from '../lib/cn';
import {toggleVariants} from './style';

const ToggleGroupContext = React.createContext<VariantProps<typeof toggleVariants>>({
  size: 'default',
  variant: 'default',
  theme: 'default',
});

const ToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> &
    VariantProps<typeof toggleVariants>
>(({className, variant, size, theme, children, ...props}, ref) => {
  const isTabVariant = variant === 'tab';

  return (
    <ToggleGroupPrimitive.Root
      ref={ref}
      className={cn(
        'flex items-center justify-center',
        isTabVariant
          ? 'h-auto max-h-none w-fit flex-wrap gap-[2px] rounded-md border border-[var(--toggle-group-border)] bg-[var(--toggle-group-bg)] p-[2px]'
          : 'w-full flex-wrap gap-[10px]',
        className,
      )}
      {...props}
    >
      <ToggleGroupContext.Provider value={{variant, size, theme}}>
        {children}
      </ToggleGroupContext.Provider>
    </ToggleGroupPrimitive.Root>
  );
});

ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName;

const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> &
    VariantProps<typeof toggleVariants>
>(({className, children, variant, size, theme, ...props}, ref) => {
  const context = React.useContext(ToggleGroupContext);
  const resolvedVariant = context.variant || variant;
  const isTabVariant = resolvedVariant === 'tab';

  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      className={cn(
        toggleVariants({
          variant: resolvedVariant,
          size: context.size || size,
          theme: context.theme || theme,
        }),
        className,
      )}
      {...props}
    >
      {isTabVariant ? (
        <span className="inline-grid">
          <span className="invisible col-start-1 row-start-1 font-semibold">
            {children}
          </span>
          <span className="col-start-1 row-start-1">{children}</span>
        </span>
      ) : (
        children
      )}
    </ToggleGroupPrimitive.Item>
  );
});

ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName;

export {ToggleGroup, ToggleGroupItem};
